import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import CapterraPixel from 'common/ads/CapterraPixel';
import AJAX from 'common/AJAX';
import { TrackEventContext } from 'common/containers/EventContainer';
import Dropdown from 'common/Dropdown';
import Button from 'common/inputs/Button';
import TextInput from 'common/inputs/TextInput';
import LandingLogos from 'common/LandingLogos';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import LocalStorage from 'common/storage/LocalStorage';
import getCookies from 'common/util/getCookies';
import parseAPIResponse from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';
import validateInput from 'common/validateInput';

import ZebImage from 'img/zeb.jpg';

import 'css/components/www/_LandingRequestDemo.scss';

class LandingRequestDemo extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  state = {
    error: null,
    isVIP: false,
    offerDemo: false,
    submitted: false,
    submitting: false,
  };

  constructor(props) {
    super(props);

    this.companyNameRef = React.createRef();
    this.companySizeRef = React.createRef();
    this.emailRef = React.createRef();
    this.firstNameRef = React.createRef();
    this.lastNameRef = React.createRef();
    this.planRef = React.createRef();
  }

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Demo Request Page');
  }

  onRequestDemo = () => {
    if (this.state.submitting || this.state.submitted) {
      return;
    }

    this.setState({
      error: null,
    });

    const email = this.emailRef.current.getValue().trim();
    const firstName = this.firstNameRef.current.getValue().trim();
    const lastName = this.lastNameRef.current.getValue().trim();
    const companyName = this.companyNameRef.current.getValue().trim();
    const companySize = this.companySizeRef.current.getValue();
    const plan = this.planRef.current.getValue();

    if (!validateInput.email(email)) {
      this.setState({
        error: 'Please enter a valid email address.',
      });
      return;
    } else if (firstName.length === 0 || lastName.length === 0) {
      this.setState({
        error: 'Please enter your name.',
      });
      return;
    } else if (companyName.length === 0) {
      this.setState({
        error: "Please enter your company's name.",
      });
      return;
    } else if (!companySize) {
      this.setState({
        error: "Please select your company's size.",
      });
      return;
    } else if (!plan) {
      this.setState({
        error: 'Please select the plan you are considering.',
      });
      return;
    }

    this.setState({
      submitting: true,
    });

    const [hubspotutk] = getCookies(['hubspotutk']);
    const referralCode = LocalStorage.getWithExpiry('referralCode');

    AJAX.post(
      '/api/demo/request',
      {
        companyName: companyName,
        companySize: companySize,
        email: email,
        hubspotutk,
        firstName,
        lastName,
        plan,
        referralCode,
      },
      async (responseString) => {
        const { error, parsedResponse } = parseAPIResponse(responseString, {
          isSuccessful: (response) =>
            response.hasOwnProperty('isVIP') &&
            response.hasOwnProperty('isQualified') &&
            response.hasOwnProperty('offerDemo'),
        });

        if (error) {
          this.setState({
            error: error.message,
            submitting: false,
          });
          return;
        }

        const { isQualified, isVIP, offerDemo } = parsedResponse;
        this.setState({
          error: null,
          isQualified,
          isVIP,
          offerDemo,
          submitting: false,
          submitted: true,
        });

        const { trackEvent } = this.props;
        trackEvent('Demo Request');

        if (isVIP) {
          trackEvent('VIP Demo Request');
        }

        if (isQualified) {
          trackEvent('Qualified Demo Request');
        }
      },
    );
  };

  renderErrorMessage() {
    if (!this.state.error) {
      return;
    }

    return <div className="error">{this.state.error}</div>;
  }

  renderPixel() {
    const { isQualified, isVIP, submitted } = this.state;
    if (!submitted) {
      return null;
    }

    if (!isQualified && !isVIP) {
      return null;
    }

    return <CapterraPixel />;
  }

  renderSubmitted() {
    const { offerDemo } = this.state;
    if (offerDemo) {
      const demoLink = `https://go.canny.io/intro-to-canny`;
      return (
        <div className="demoRequestSuccess">
          <div className="title">Great, please choose a time.</div>
          <div className="description">We're excited to speak with you!</div>
          <div className="bookButton">
            <a href={demoLink} target="_blank" rel="noopener">
              <Button buttonType="cannyButton" value="Go to booking page" />
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="demoRequestSuccess">
        <div className="title">Great, we'll be in touch!</div>
        <div className="description">
          In the meantime, take a look at our <Link to="/features">features</Link> and{' '}
          <Link to="/integrations">integrations</Link> or get started with Canny for&nbsp;
          <Link to="/register">free</Link>.
        </div>
      </div>
    );
  }

  renderRequestForm() {
    const { submitted } = this.state;
    if (submitted) {
      return this.renderSubmitted();
    }

    return (
      <div className="form">
        <div className="title">Help us personalize your demo</div>
        <TextInput autoFocus={true} placeholder="Work email" ref={this.emailRef} />
        <div className="inputPair">
          <TextInput placeholder="First name" ref={this.firstNameRef} />
          <TextInput placeholder="Last name" ref={this.lastNameRef} />
        </div>
        <div className="inputPair">
          <TextInput placeholder="Company name" ref={this.companyNameRef} />
          <Dropdown
            options={[
              { name: '1-10', render: '1-10' },
              { name: '11-50', render: '11-50' },
              { name: '51-100', render: '51-100' },
              { name: '101-500', render: '101-500' },
              { name: '500+', render: '500+' },
            ]}
            placeholder="Company size"
            ref={this.companySizeRef}
          />
        </div>
        <Dropdown
          options={[
            { name: 'free', render: 'Free' },
            { name: 'core', render: 'Core' },
            { name: 'pro', render: 'Pro' },
            { name: 'business', render: 'Business' },
          ]}
          placeholder="Which plan are you considering?"
          ref={this.planRef}
        />
        {this.renderErrorMessage()}
        <Button
          buttonType="cannyButton"
          loading={this.state.submitting}
          onTap={this.onRequestDemo}
          value="Submit"
        />
      </div>
    );
  }

  render() {
    return (
      <div className="landingRequestDemo">
        <div className="mainContentContainer">
          <div className="left">
            <h1 className="heading">See Canny in action with a demo from our&nbsp;team</h1>
            <div className="testimonial">
              <div className="quote">
                “Canny intrinsically provides us with a way of connecting and communicating with the
                people that matter most to our&nbsp;product.”
              </div>
              <div className="attribution">
                <LazyLoadedImage loading="eager" src={ZebImage} />
                <div className="text">
                  <div className="name">Zeb Evans</div>
                  <div className="title">CEO at ClickUp</div>
                </div>
              </div>
            </div>
          </div>
          <div className="right">
            {this.renderRequestForm()}
            {this.renderPixel()}
          </div>
        </div>
        <LandingLogos loading="eager" />
      </div>
    );
  }
}

export default compose(withContexts({ trackEvent: TrackEventContext }), hot)(LandingRequestDemo);
